import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { api } from "../lib/api";

const sessionKey = "gens-chat-session";
const getSessionId = () => {
  let id = localStorage.getItem(sessionKey);
  if (!id) {
    id = `session-${Math.random().toString(36).slice(2, 10)}-${Date.now()}`;
    localStorage.setItem(sessionKey, id);
  }
  return id;
};

const STARTER = [
  "I'm looking to buy a home in Houston",
  "I want to refinance my mortgage",
  "I'm an investor — tell me about DSCR loans",
];

export default function Chatbot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Hi! I'm Ava, your Gens Realty concierge. Whether you're buying, selling, refinancing, or investing — I can help. What's bringing you to Houston real estate today?",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading, open]);

  const send = async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setLoading(true);
    try {
      const { data } = await api.post("/chat", {
        session_id: getSessionId(),
        message: text,
      });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            "Sorry, I'm having trouble responding right now. Please try again or contact us at (832) 741-4662.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-[#E63946] hover:bg-[#c9313d] text-white shadow-lg shadow-red-500/30 flex items-center justify-center transition-all hover:scale-105"
        aria-label="Open AI chat"
        data-testid="chatbot-toggle"
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>

      {/* Panel */}
      {open && (
        <div
          className="fixed bottom-24 right-6 z-50 w-[calc(100vw-3rem)] sm:w-96 h-[32rem] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden"
          data-testid="chatbot-panel"
        >
          {/* Header */}
          <div className="bg-[#1A1A1A] text-white px-5 py-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[#E63946] flex items-center justify-center">
              <Sparkles className="w-4 h-4" strokeWidth={1.75} />
            </div>
            <div className="flex-1">
              <div className="font-display font-semibold">Ava</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
                Gens Realty AI Concierge
              </div>
            </div>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto p-4 space-y-3 bg-stone-50"
          >
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-[#E63946] text-white rounded-br-sm"
                      : "bg-white text-[#1A1A1A] border border-gray-200 rounded-bl-sm"
                  }`}
                  data-testid={`chat-message-${m.role}`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-gray-200 px-4 py-3 rounded-2xl rounded-bl-sm">
                  <div className="flex gap-1.5">
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" />
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce [animation-delay:120ms]" />
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce [animation-delay:240ms]" />
                  </div>
                </div>
              </div>
            )}

            {/* Quick prompts */}
            {messages.length === 1 && !loading && (
              <div className="pt-2 space-y-2">
                {STARTER.map((s) => (
                  <button
                    key={s}
                    onClick={() => send(s)}
                    className="w-full text-left text-xs px-3 py-2 bg-white border border-gray-200 rounded-lg hover:border-[#E63946] hover:text-[#E63946] transition-colors"
                    data-testid="chat-starter-prompt"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Input */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
            className="border-t border-gray-200 p-3 bg-white flex gap-2"
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about Houston homes..."
              className="flex-1 px-4 py-2.5 rounded-lg bg-stone-50 border border-gray-200 text-sm focus:outline-none focus:border-[#E63946]"
              data-testid="chat-input"
            />
            <button
              type="submit"
              disabled={loading || !input.trim()}
              className="bg-[#E63946] hover:bg-[#c9313d] disabled:opacity-50 text-white w-10 h-10 rounded-lg flex items-center justify-center"
              data-testid="chat-send"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
